/**
 * apps/customer/src/components/legal/PrivacyPolicyContent.tsx
 *
 * SIL-1057: the Privacy Policy body, extracted out of routes/privacy.tsx so
 * both the authenticated /privacy route and SignupFrame's in-page panel
 * render the exact same text from one place. A signed-out referral visitor
 * never reaches /privacy (it sits behind the auth gate — __root.tsx's
 * isPublicSignupPath only exempts /auth/callback and /signup), so the
 * signup footer's Privacy link now opens this component inline instead of
 * navigating there.
 *
 * Static content only, mirrored from docs/legal/privacy-policy.md — no
 * markdown renderer is a dependency of this app, so any edit to the policy
 * needs both files updated. No auth, no data fetching: safe to render
 * anywhere, including from an unauthenticated route.
 */
export function PrivacyPolicyContent() {
  return (
    <>
      <p>
        Silicon Exchange, Corp ("Silicon Exchange," "SiliconX," "we," "us," or "our") operates
        the SiliconX platform, including our customer portal (for buyer and seller organization
        users), our public exchange site (exchange.siliconx.tech), and our internal
        analyst/operations portal (together, the "Services"). This
        Privacy Policy explains what personal data we collect, how we use it, and the rights
        available to you.
      </p>
      <p>This policy applies to:</p>
      <ul className="ml-5 list-disc space-y-1">
        <li>
          <strong>Customer portal users</strong>, located in the European Union, United Kingdom,
          United States, or California, who use the Services to buy or sell hardware assets on
          behalf of their organization.
        </li>
        <li>
          <strong>Operations/analyst portal users</strong>, located in the United States or
          California, who are Silicon Exchange staff or contractors.
        </li>
      </ul>

      <section>
        <h2 className="text-base font-semibold text-foreground">1. Personal Data We Collect</h2>
        <p className="mt-2">We collect the minimum personal data needed to operate the Services:</p>
        <ul className="ml-5 mt-2 list-disc space-y-1">
          <li>
            <strong>Email address</strong> — used to create and authenticate your account (via
            Amazon Cognito) and to send account-related notices (invitations, verification,
            password resets).
          </li>
          <li>
            <strong>Account and session data</strong> — your account role, organization
            association, and login session state, needed to control access to the Services.
          </li>
          <li>
            <strong>Audit log data</strong> — a record of certain account actions (for example,
            invitations, deactivations, role changes) tied to your account, for security and
            compliance purposes.
          </li>
          <li>
            <strong>Mobile phone number</strong> — collected only if you enter it to verify a
            buyer account, and used only as described in Section 11 (SMS and Mobile Numbers).
          </li>
        </ul>
        <p className="mt-2">
          Other than a mobile number you choose to enter for buyer verification, we do not
          collect your name, phone number, or physical address through the Services. We use
          cookies and similar technologies from Google Analytics and HubSpot to understand how
          visitors use our websites, as described in Section 3. If you are in the European
          Economic Area, the United Kingdom, or Switzerland, these analytics cookies are set only
          after you accept them in our cookie banner. Before you accept, our websites still load
          the Google Analytics and HubSpot scripts and send Google measurement signals that
          contain no cookies or identifiers. Elsewhere, you can turn them off at any time with
          the "
          {/* SIL-1529: /privacy and the signup legal panel have no AppShell
              footer; the shared analytics module opens the HubSpot banner on
              any data-cookie-settings click. */}
          <button
            type="button"
            data-cookie-settings=""
            data-track="privacy_cookie_settings"
            className="text-brand-600 underline"
          >
            Cookie settings
          </button>
          " link in the site footer or in this policy. The portal also uses browser session
          storage (<code>sessionStorage</code>) to hold your login session while you are actively
          using it; this data is cleared when your browser session ends. The public exchange site
          keeps a short list of listings you viewed in your browser's local storage
          (<code>localStorage</code>); it is sent to us only if you submit a request form.
        </p>
        <p className="mt-2">
          Organizations using the Services also provide business information (for example,
          company legal name) about themselves. This organizational information is not personal
          data about an individual.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">2. How We Use Personal Data</h2>
        <p className="mt-2">We use personal data only to:</p>
        <ul className="ml-5 mt-2 list-disc space-y-1">
          <li>Create, authenticate, and secure your account.</li>
          <li>
            Operate core platform features (buying, selling, and managing hardware assets and
            listings on behalf of your organization).
          </li>
          <li>Send you service-related communications (invitations, verification, security notices).</li>
          <li>Maintain audit logs for security, fraud prevention, and legal compliance.</li>
          <li>
            Measure and improve how visitors use the Services, using the analytics providers
            described in Section 3.
          </li>
          <li>Respond to requests you submit, using the CRM provider described in Section 3.</li>
          <li>Comply with legal obligations.</li>
        </ul>
        <p className="mt-2">
          We do not sell personal data. We do not use personal data for advertising or share it
          with third parties for their own marketing purposes.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">3. How We Share Personal Data</h2>
        <p className="mt-2">We share personal data only as follows:</p>
        <ul className="ml-5 mt-2 list-disc space-y-1">
          <li>
            <strong>Cloud infrastructure provider.</strong> We use Amazon Web Services (AWS),
            located in the United States, to host the Services and to manage account
            authentication. AWS processes your email address and account/session data on our
            behalf, under a data processing agreement, solely to provide these hosting and
            authentication functions.
          </li>
          <li>
            <strong>Legal and safety.</strong> We may disclose personal data if required by law,
            or to protect the rights, safety, or property of Silicon Exchange, our users, or the
            public.
          </li>
          <li>
            <strong>Business transfer.</strong> If Silicon Exchange is involved in a merger,
            acquisition, or sale of assets, personal data may be transferred as part of that
            transaction, subject to this policy.
          </li>
          <li>
            <strong>Analytics provider.</strong> We use Google Analytics (Google LLC) to measure
            how visitors use the Services, such as pages viewed, buttons clicked, approximate
            location derived from IP address, device and browser type, and referring site. For
            signed-in users we send Google a pseudonymous identifier derived from your account ID,
            never your name or email address. Google Analytics keeps this data for 14 months, and
            we keep a copy in our own analytics database for no longer than 14 months.
          </li>
          <li>
            <strong>CRM provider.</strong> We use HubSpot, Inc. to manage customer relationships.
            When you submit a request form or create an account, HubSpot receives your email
            address and links it to the pages you viewed on our websites, so we can respond to
            you.
          </li>
        </ul>
        <p className="mt-2">
          We do not use advertising trackers, and we do not let Google or HubSpot use your data
          for their own advertising. Our market-data research uses only publicly available
          third-party listings and does not involve your personal data.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">4. International Data Transfers</h2>
        <p className="mt-2">
          The Services and the personal data we collect are processed in the United States. If
          you are located in the European Union or United Kingdom, your personal data is
          transferred to the United States. We rely on Standard Contractual Clauses approved by
          the European Commission to lawfully transfer this data.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">5. Data Retention</h2>
        <p className="mt-2">
          We retain your account data for as long as your account is active. If your account is
          closed, we delete or anonymize your account data within 90 days. We retain audit log
          data for 7 years, consistent with standard financial recordkeeping practice, after
          which it is deleted.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">6. Your Privacy Rights</h2>
        <p className="mt-2">Depending on where you live, you have rights over your personal data.</p>
        <p className="mt-2">
          <strong>If you are in the European Union or United Kingdom (GDPR):</strong> you have
          the right to access, correct, delete, or export your personal data, to restrict or
          object to certain processing, and to withdraw consent where processing relies on
          consent. You also have the right to lodge a complaint with your local data protection
          authority.
        </p>
        <p className="mt-2">
          <strong>If you are a California resident (CCPA/CPRA):</strong> you have the right to
          know what personal data we collect, to request deletion, to correct inaccurate data,
          and to opt out of the sale or sharing of personal data. We do not sell or share
          personal data, so no opt-out is needed for that.
        </p>
        <p className="mt-2">
          <strong>How to exercise your rights:</strong> Send your request to{' '}
          <a href="mailto:privacy@siliconx.tech" className="text-brand-600 underline">
            privacy@siliconx.tech
          </a>
          . We will respond within 30 days. We may need to verify your identity before
          completing your request.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">7. Children's Privacy</h2>
        <p className="mt-2">
          The Services are intended for business use by adults acting on behalf of an
          organization. The Services are not directed to, and we do not knowingly collect
          personal data from, anyone under 18 years of age.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">8. Security</h2>
        <p className="mt-2">
          We use industry-standard technical and organizational measures, including encrypted
          authentication and access controls, to protect personal data. No system is completely
          secure, and we cannot guarantee absolute security.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">9. Changes to This Policy</h2>
        <p className="mt-2">
          We may update this policy from time to time. We will post the updated policy with a
          new effective date. Material changes will be communicated to registered users through
          the Services.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">10. Governing Law</h2>
        <p className="mt-2">
          This policy is governed by the laws of the State of Delaware, without regard to its
          conflict-of-laws principles, except where applicable data protection law (such as
          GDPR or CCPA/CPRA) requires otherwise.
        </p>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">11. SMS and Mobile Numbers</h2>
        <ul className="ml-5 mt-2 list-disc space-y-1">
          <li>
            We collect a mobile phone number only when you enter it for account or buyer
            verification. We use it only to send one-time verification codes and related account
            notices by SMS.
          </li>
          <li>
            Mobile numbers and SMS opt-in consent are not sold, rented, or shared with third
            parties or affiliates for marketing or promotional purposes. We share your number
            only with the SMS delivery providers we use to send these messages.
          </li>
          <li>
            Message frequency varies based on your actions. Message and data rates may apply.
          </li>
          <li>
            Reply STOP to opt out at any time, or HELP for help. You can also contact us at{' '}
            <a href="mailto:support@siliconx.com" className="text-brand-600 underline">
              support@siliconx.com
            </a>
            .
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-base font-semibold text-foreground">12. Contact Us</h2>
        <p className="mt-2">
          Silicon Exchange, Corp
          <br />
          Email:{' '}
          <a href="mailto:privacy@siliconx.tech" className="text-brand-600 underline">
            privacy@siliconx.tech
          </a>
        </p>
      </section>
    </>
  );
}
